import { getAllCities, getAllSubdivisionsForCountry } from '@freighthub/reference-master-data'
import { Autocomplete, AutocompleteValue, Grid, TextField } from '@freighthub/silhouette-core'
import { get, isString } from 'lodash-es'
import { useObserver } from 'mobx-react-lite'
import { FC, useEffect } from 'react'
import { isEUCountryCode } from 'service/ISO'
import { LanguageISO, useI18n } from 'utils/localisation'

import { isoCountriesDescriptions } from './countriesDescriptions'
import { TLegalEntity } from './LegalEntitiesModel'

export type BillingAddressProps = {
  legalEntity: TLegalEntity
  disabled?: boolean
}

export const billingAddressTestIds = {
  countryAutoComplete: 'billing_country-autocomplete',
  stateAutoComplete: 'billing_state-autocomplete',
  cityAutoComplete: 'billing_city-autocomplete',
  street: 'billing_street_text',
}

export const getCountryName =
  (currentLanguage: LanguageISO) =>
  (countryCode: string): string => {
    const countryName = get(isoCountriesDescriptions, [countryCode, currentLanguage]) as string
    return countryName ? `${countryName} (${countryCode})` : countryCode
  }

export const BillingAddress: FC<BillingAddressProps> = ({ legalEntity, disabled = false }) => {
  const { translate, current: currentLanguage } = useI18n()
  const getCountryNameFromCode = getCountryName(currentLanguage)

  const { billingAddress, isEU } = legalEntity

  const countryOptions = Object.keys(isoCountriesDescriptions)
    .filter((countryCode) => (isEU ? isEUCountryCode(countryCode) : !isEUCountryCode(countryCode)))
    .map((countryCode) => ({
      text: getCountryNameFromCode(countryCode),
      value: countryCode,
    }))

  const subdivisionsOptions =
    getAllSubdivisionsForCountry(billingAddress.countryCode.value).map((subdivision) => ({
      text: subdivision,
      value: subdivision,
    })) ?? []

  const citiesOptions =
    getAllCities(billingAddress.countryCode.value).map((city) => ({
      text: city,
      value: city,
    })) ?? []

  useEffect(() => {
    if (
      billingAddress.countryCode.value &&
      subdivisionsOptions.map((option) => option.value).indexOf(billingAddress.state.value) === -1
    ) {
      billingAddress.state.setValue('') // Clear state field when state is not in options
    }
  }, [billingAddress.countryCode.value])

  return useObserver(() => (
    <>
      <Grid xs={12}>
        <TextField
          disabled={disabled}
          fullWidth
          value={billingAddress.street.value}
          label={translate('directory-form_street_1')}
          onChange={(e) => billingAddress.update('street', e.target.value)}
          helperText={billingAddress.validateStreet().isInvalid && translate(billingAddress.validateStreet().message)}
          error={billingAddress.validateStreet().isInvalid}
          data-testid={billingAddressTestIds.street}
          required
        />
      </Grid>
      <Grid xs={12}>
        <TextField
          disabled={disabled}
          fullWidth
          label={translate('directory-form_street_2')}
          value={billingAddress.street2.value}
          onChange={(e) => billingAddress.update('street2', e.target.value)}
        />
      </Grid>
      <Grid xs={4}>
        <Autocomplete
          disabled={disabled}
          value={{
            text: billingAddress.city.value,
          }}
          freeSolo
          onClose={(event) => {
            const { value } = event.target as HTMLInputElement
            if (isString(value)) {
              return billingAddress.update('city', value)
            }
          }}
          onOptionSelect={(option) => {
            return billingAddress.update('city', option.value)
          }}
          options={citiesOptions}
          label={translate('directory-form_city')}
          TextFieldProps={{
            margin: 'none',
            required: true,
            error: billingAddress.validateCity().isInvalid,
            helperText: billingAddress.validateCity().isInvalid && translate(billingAddress.validateCity().message),
          }}
          data-testid={billingAddressTestIds.cityAutoComplete}
        />
      </Grid>
      <Grid xs={4}>
        <TextField
          disabled={disabled}
          fullWidth
          label={translate('directory-form_postal_code')}
          value={billingAddress.postalCode.value}
          onChange={(e) => billingAddress.update('postalCode', e.target.value)}
          helperText={
            billingAddress.validatePostalCode().isInvalid && translate(billingAddress.validatePostalCode().message)
          }
          error={billingAddress.validatePostalCode().isInvalid}
          required
        />
      </Grid>
      <Grid xs={4}>
        <Autocomplete
          disabled={disabled}
          value={{
            text: billingAddress.state.value,
          }}
          getOptionSelected={(option) => option.text}
          onChange={(_, state: AutocompleteValue) => billingAddress.update('state', get(state, 'value', ''))}
          options={subdivisionsOptions}
          label={translate('directory-form_state')}
          TextFieldProps={{
            margin: 'none',
          }}
          data-testid={billingAddressTestIds.stateAutoComplete}
        />
      </Grid>
      <Grid xs={12}>
        <Autocomplete
          disabled={disabled}
          value={{
            text:
              getCountryNameFromCode(billingAddress.countryCode.value) ||
              getCountryNameFromCode(billingAddress.country.value),
          }}
          getOptionSelected={(option) => option.text}
          disableClearable={billingAddress.validateCountry().isInvalid}
          onChange={(_, country: AutocompleteValue) => billingAddress.update('countryCode', get(country, 'value', ''))}
          options={countryOptions}
          label={translate('directory-form_country')}
          TextFieldProps={{
            error: billingAddress.validateCountry().isInvalid,
            helperText:
              billingAddress.validateCountry().isInvalid && translate(billingAddress.validateCountry().message),
            required: true,
            margin: 'none',
          }}
          data-testid={billingAddressTestIds.countryAutoComplete}
        />
      </Grid>
    </>
  ))
}
